type GtagFn = (...args: unknown[]) => void;
type FbqFn = (...args: unknown[]) => void;
type LintrkFn = (action: string, data: unknown) => void;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: GtagFn;
    fbq?: FbqFn;
    lintrk?: LintrkFn;
  }
}

function gtagSafe(...args: unknown[]) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(args);
}

// Google Ads conversion label IDs — fill in once Google Ads account is created.
// Format: 'AW-XXXXXXXXXX/YYYYYYYYYY' (conversion ID / conversion label)
export const GOOGLE_ADS_CONVERSIONS = {
  lead: "",
  phoneCall: "",
  contactForm: "",
};

export interface LeadEventPayload {
  source?: string;
  page?: string;
  value?: number;
}

/**
 * Fires a standardized Lead conversion across GA4, Meta Pixel, LinkedIn, and Google Ads.
 * Respects consent — platforms only receive events if user opted in.
 */
export function trackLead(payload: LeadEventPayload = {}) {
  const data = {
    event_category: "lead",
    event_label: payload.source || "contact_form",
    page_path: payload.page || window.location.pathname,
    value: payload.value ?? 1,
  };

  gtagSafe("event", "generate_lead", data);

  if (GOOGLE_ADS_CONVERSIONS.lead) {
    gtagSafe("event", "conversion", { send_to: GOOGLE_ADS_CONVERSIONS.lead });
  }

  if (typeof window.fbq === "function") {
    window.fbq("track", "Lead", {
      content_name: data.event_label,
      content_category: "inspection_services",
    });
  }

  if (typeof window.lintrk === "function") {
    window.lintrk("track", { conversion_id: "lead" });
  }
}

export function trackPhoneClick(href: string) {
  gtagSafe("event", "phone_click", {
    event_category: "contact",
    event_label: href,
  });
  if (GOOGLE_ADS_CONVERSIONS.phoneCall) {
    gtagSafe("event", "conversion", { send_to: GOOGLE_ADS_CONVERSIONS.phoneCall });
  }
  if (typeof window.fbq === "function") {
    window.fbq("track", "Contact");
  }
}

export function trackContactFormSubmit(formId = "contact_form") {
  trackLead({ source: formId });
  if (GOOGLE_ADS_CONVERSIONS.contactForm) {
    gtagSafe("event", "conversion", { send_to: GOOGLE_ADS_CONVERSIONS.contactForm });
  }
}

export function captureUtmParams(): Record<string, string> {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  const utm: Record<string, string> = {};
  ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "gclid", "fbclid"].forEach((key) => {
    const v = params.get(key);
    if (v) utm[key] = v;
  });
  if (Object.keys(utm).length > 0) {
    try {
      const existing = JSON.parse(sessionStorage.getItem("arcondes_utm") || "{}");
      sessionStorage.setItem("arcondes_utm", JSON.stringify({ ...existing, ...utm }));
    } catch {}
  }
  return utm;
}

export function readStoredUtm(): Record<string, string> {
  try {
    return JSON.parse(sessionStorage.getItem("arcondes_utm") || "{}");
  } catch {
    return {};
  }
}
